// components/CompanyAnalytics.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import {
  FiPieChart, FiBarChart2, FiTrendingUp, FiAlertCircle,
  FiCheckCircle, FiXCircle, FiHome, FiDollarSign,
  FiRefreshCw, FiDownload, FiMapPin, FiCreditCard,
  FiAward, FiSearch, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiWifi, FiWifiOff,
  FiDatabase, FiClock, FiMenu, FiX, FiActivity,
  FiLayers, FiTarget, FiPercent, FiEye,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

// -------------------- UTILITAIRES GLOBAUX --------------------
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '0 FC';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' FC';
};

const formatDateCache = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Date invalide';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

// -------------------- BASE DE DONNÉES & UTILITAIRES --------------------
const db = new Dexie('CompanyAnalyticsCache');
db.version(1).stores({
  companies: 'id, name, nif, rccm, totalTTC, cacheId',
  cacheInfo: 'key',
  requestLog: '++id, timestamp, endpoint',
});

class RateLimiter {
  // ... (inchangé)
  constructor(maxRequests = 5, timeWindow = 60000) {
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
    this.requests = [];
    this.requestQueue = [];
    this.isProcessing = false;
  }

  async acquire() {
    return new Promise((resolve) => {
      this.requestQueue.push(resolve);
      this.processQueue();
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;
    while (this.requestQueue.length > 0) {
      const now = Date.now();
      this.requests = this.requests.filter(time => now - time < this.timeWindow);
      if (this.requests.length < this.maxRequests) {
        const resolve = this.requestQueue.shift();
        this.requests.push(now);
        resolve();
      } else {
        const oldestRequest = this.requests[0];
        const waitTime = this.timeWindow - (now - oldestRequest);
        await new Promise(resolve => setTimeout(resolve, waitTime + 100));
      }
    }
    this.isProcessing = false;
  }
}

const fetchWithRetry = async (url, options = {}, retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
};

const rateLimiter = new RateLimiter(3, 60000);

const logRequest = async (endpoint) => {
  try {
    await db.requestLog.add({ endpoint, timestamp: new Date().toISOString() });
    const count = await db.requestLog.count();
    if (count > 100) {
      const oldest = await db.requestLog.orderBy('id').first();
      if (oldest) await db.requestLog.delete(oldest.id);
    }
  } catch (error) {
    console.error('Erreur logging:', error);
  }
};

const isCacheValid = async () => {
  try {
    const cacheInfo = await db.cacheInfo.get('company_analytics');
    if (!cacheInfo) return false;
    const now = new Date();
    const cacheDate = new Date(cacheInfo.timestamp);
    const hoursDiff = (now - cacheDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  } catch (error) {
    console.error('Erreur cache:', error);
    return false;
  }
};

const saveToCache = async (companies) => {
  try {
    await db.companies.clear();
    const companiesWithId = companies.map((company, index) => ({ ...company, cacheId: index + 1 }));
    if (companiesWithId.length > 0) {
      const batchSize = 50;
      for (let i = 0; i < companiesWithId.length; i += batchSize) {
        const batch = companiesWithId.slice(i, i + batchSize);
        await db.companies.bulkAdd(batch);
      }
    }
    await db.cacheInfo.put({ key: 'company_analytics', timestamp: new Date().toISOString() });
  } catch (error) {
    console.error('Erreur sauvegarde cache:', error);
  }
};

const loadFromCache = async () => {
  try {
    const companies = await db.companies.toArray();
    return companies.map(({ cacheId, ...rest }) => rest);
  } catch (error) {
    console.error('Erreur chargement cache:', error);
    return [];
  }
};

const clearCache = async () => {
  try {
    await db.companies.clear();
    await db.cacheInfo.clear();
    console.log('Cache vidé');
  } catch (error) {
    console.error('Erreur vidage cache:', error);
  }
};

// -------------------- COMPOSANTS RÉUTILISABLES --------------------
const Badge = ({ children, color = 'blue' }) => {
  const colors = {
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${colors[color] || colors.blue}`}>
      {children}
    </span>
  );
};

const CompanyCardMobile = ({ company, rank, onClick }) => {
  const [expanded, setExpanded] = useState(false);
  const rankStyle = rank === 1
    ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black'
    : rank === 2
    ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black'
    : rank === 3
    ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white'
    : 'bg-blue-500/20 text-blue-400';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 mb-3"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${rankStyle}`}>
          {rank <= 3 ? <FiAward size={18} /> : rank}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm truncate">{company.name || 'N/A'}</h3>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="p-1 text-slate-400 hover:text-white">
          {expanded ? <FiChevronRight size={16} /> : <FiChevronLeft size={16} />}
        </button>
      </div>

      <div className="space-y-2 text-sm text-slate-300 mb-3">
        <div className="flex justify-between">
          <span className="text-slate-400">NIF:</span>
          <span className={company.hasNif ? 'text-emerald-400' : 'text-red-400'}>
            {company.hasNif ? company.nif : 'Manquant'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">RCCM:</span>
          <span className={company.hasRccm ? 'text-emerald-400' : 'text-red-400'}>
            {company.hasRccm ? company.rccm : 'Manquant'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-400">CA TTC:</span>
          <span className="text-emerald-400 font-semibold">{formatCurrency(company.totalTTC)}</span>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-3 border-t border-white/10 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Adresse</span><span className="text-slate-200">{company.address || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Total HT</span><span className="text-slate-200">{formatCurrency(company.totalHT)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">TVA (16%)</span><span className="text-amber-400">{formatCurrency(company.taxAmount)}</span></div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => onClick(company)}
        className="w-full mt-3 py-2 rounded-xl bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-medium flex items-center justify-center gap-1 hover:bg-blue-500/20 transition-colors"
      >
        <FiEye size={14} /> Détails
      </button>
    </motion.div>
  );
};

const MobileMenu = ({ isOpen, onClose, onRefresh, onClearCache, onExport, isRefreshing }) => (
  <AnimatePresence>
    {isOpen && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={onClose}
        />
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 25 }}
          className="fixed right-0 top-0 h-full w-72 bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border-l border-blue-400/20 shadow-2xl z-50 p-6"
        >
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <button onClick={onClose} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={18} /></button>
          </div>
          <div className="space-y-4">
            <button onClick={onRefresh} disabled={isRefreshing} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 disabled:opacity-50">
              <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
            </button>
            <button onClick={onClearCache} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400">
              <FiDatabase /> Vider le cache
            </button>
            <button onClick={onExport} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400">
              <FiDownload /> Exporter CSV
            </button>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const DonutChart = ({ data, total, size = 200 }) => {
  const radius = size / 2;
  let currentAngle = -90;
  if (total === 0) return <div className="flex items-center justify-center h-48 text-slate-400">Aucune donnée</div>;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {data.map((item, idx) => {
          const angle = (item.value / total) * 360;
          if (angle === 0) return null;
          const startAngle = currentAngle;
          const endAngle = currentAngle + angle;
          currentAngle = endAngle;
          const startRad = (startAngle * Math.PI) / 180;
          const endRad = (endAngle * Math.PI) / 180;
          const x1 = radius + radius * Math.cos(startRad);
          const y1 = radius + radius * Math.sin(startRad);
          const x2 = radius + radius * Math.cos(endRad);
          const y2 = radius + radius * Math.sin(endRad);
          const largeArc = angle > 180 ? 1 : 0;
          return (
            <path
              key={idx}
              d={`M ${radius} ${radius} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`}
              fill={item.color}
              stroke="rgba(15,25,55,0.6)"
              strokeWidth="2"
            />
          );
        })}
        <circle cx={radius} cy={radius} r={radius * 0.6} fill="rgba(15,25,55,0.8)" />
        <text x={radius} y={radius} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="20" fontWeight="bold">{total}</text>
        <text x={radius} y={radius + 22} textAnchor="middle" dominantBaseline="middle" fill="rgba(170,200,255,0.6)" fontSize="10">Entreprises</text>
      </svg>
      <div className="flex flex-wrap gap-3 mt-4 justify-center">
        {data.map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ background: item.color }}></div>
            <span className="text-slate-300">{item.name}</span>
            <span className="text-white font-semibold">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProgressBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-300">{label}</span>
        <span className="text-slate-400">{value} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${color}, ${color}cc)` }}
        />
      </div>
    </div>
  );
};

// -------------------- COMPOSANT PRINCIPAL --------------------
const CompanyAnalytics = () => {
  // ... tout le reste du code (inchangé)
  // Mais attention : on enlève les définitions locales de formatCurrency et formatDateCache
  // car elles sont maintenant globales.

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cacheDate, setCacheDate] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const lastFetchRef = useRef(null);
  const fetchTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const debouncedFetch = useCallback((callback, delay = 1000) => {
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(callback, delay);
  }, []);

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      if (!forceRefresh && lastFetchRef.current && Date.now() - lastFetchRef.current < 10000) {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          return;
        }
      }
      setLoading(true);
      setError(null);
      if (!forceRefresh) {
        const cacheValid = await isCacheValid();
        if (cacheValid) {
          const cached = await loadFromCache();
          if (cached.length) {
            setData(cached);
            const cacheInfo = await db.cacheInfo.get('company_analytics');
            if (cacheInfo) setCacheDate(cacheInfo.timestamp);
            setSuccessMessage(`📦 ${cached.length} entreprises chargées depuis le cache`);
            setTimeout(() => setSuccessMessage(null), 3000);
            if (isOnline) debouncedFetch(() => refreshDataInBackground(), 15000);
            setLoading(false);
            return;
          }
        }
      }
      await rateLimiter.acquire();
      await logRequest('company_analytics.php');
      const apiUrl = API_CONFIG?.apiUrl;
      if (!apiUrl) throw new Error('URL API manquante');
      const response = await fetchWithRetry(`${apiUrl}company_analytics.php`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        await saveToCache(result.data);
        lastFetchRef.current = Date.now();
        const cacheInfo = await db.cacheInfo.get('company_analytics');
        if (cacheInfo) setCacheDate(cacheInfo.timestamp);
        setSuccessMessage(`✅ ${result.data.length} entreprises chargées`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(result.message);
      }
    } catch (err) {
      if (err.message?.includes('429') || err.status === 429) {
        setError('Trop de requêtes. Patientez...');
        setTimeout(() => fetchData(false), 30000);
      } else {
        const cached = await loadFromCache();
        if (cached.length) {
          setData(cached);
          setError(null);
          setSuccessMessage(`📦 ${cached.length} entreprises chargées depuis le cache`);
          setTimeout(() => setSuccessMessage(null), 4000);
        } else {
          setError('Impossible de charger les données.');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [isOnline, debouncedFetch]);

  const refreshDataInBackground = async () => {
    try {
      await rateLimiter.acquire();
      const apiUrl = API_CONFIG?.apiUrl;
      if (!apiUrl) return;
      const response = await fetchWithRetry(`${apiUrl}company_analytics.php`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        await saveToCache(result.data);
        lastFetchRef.current = Date.now();
      }
    } catch (err) { /* silent */ }
  };

  const manualRefresh = async () => {
    if (!isOnline) {
      setError('Pas de connexion internet.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    setIsRefreshing(true);
    try { await fetchData(true); } catch { setError('Échec du rafraîchissement'); }
    finally { setIsRefreshing(false); }
  };

  const clearCacheAndReload = async () => {
    await clearCache();
    lastFetchRef.current = null;
    await fetchData(true);
  };

  useEffect(() => {
    fetchData();
    return () => { if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current); };
  }, [fetchData, retryCount]);

  // Analyses
  const analysis = useMemo(() => {
    return (data || []).map(item => ({
      name: item.company_name,
      address: item.address,
      nif: item.nif,
      rccm: item.rccm,
      totalHT: item.total_ht || 0,
      totalTTC: item.total_ttc || 0,
      taxAmount: item.tax_amount || 0,
      hasNif: item.nif && item.nif.trim() !== '',
      hasRccm: item.rccm && item.rccm.trim() !== '',
    }));
  }, [data]);

  const top100 = useMemo(() => {
    if (!analysis.length) return [];
    return [...analysis].sort((a, b) => b.totalTTC - a.totalTTC).slice(0, 100);
  }, [analysis]);

  const [filteredTop100, setFilteredTop100] = useState([]);
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (!searchTerm) {
        setFilteredTop100(top100);
      } else {
        const s = searchTerm.toLowerCase();
        setFilteredTop100(top100.filter(c =>
          c.name?.toLowerCase().includes(s) ||
          (c.nif && c.nif.toLowerCase().includes(s)) ||
          (c.rccm && c.rccm.toLowerCase().includes(s))
        ));
      }
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchTerm, top100]);

  const totalPages = Math.ceil(filteredTop100.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const currentCompanies = filteredTop100.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);

  const stats = useMemo(() => {
    const totalCompanies = analysis.length;
    if (totalCompanies === 0) {
      return {
        totalCompanies: 0, withBoth: 0, withNone: 0, withNifOnly: 0, withRccmOnly: 0,
        withMissingInfo: 0, completionRate: 0, missingRate: 0,
        totalRevenue: 0, totalTax: 0, averageRevenue: 0,
        top100Count: 0, top100Revenue: 0, top100Percentage: 0,
        maxRevenue: 0, minRevenue: 0,
        medianRevenue: 0, stdDevRevenue: 0, cv: 0, q1: 0, q3: 0,
        hhi: 0,
      };
    }

    const revenues = analysis.map(c => c.totalTTC).sort((a, b) => a - b);
    const totalRevenue = revenues.reduce((s, v) => s + v, 0);
    const averageRevenue = totalRevenue / totalCompanies;
    const maxRevenue = revenues[revenues.length - 1];
    const minRevenue = revenues[0];

    const median = totalCompanies % 2 === 0
      ? (revenues[totalCompanies / 2 - 1] + revenues[totalCompanies / 2]) / 2
      : revenues[Math.floor(totalCompanies / 2)];
    const q1 = revenues[Math.floor(totalCompanies * 0.25)];
    const q3 = revenues[Math.floor(totalCompanies * 0.75)];

    const variance = analysis.reduce((s, c) => s + Math.pow(c.totalTTC - averageRevenue, 2), 0) / totalCompanies;
    const stdDev = Math.sqrt(variance);
    const cv = averageRevenue !== 0 ? (stdDev / averageRevenue) * 100 : 0;

    const marketShares = analysis.map(c => (c.totalTTC / totalRevenue) * 100);
    const hhi = marketShares.reduce((sum, share) => sum + Math.pow(share, 2), 0);

    const withBoth = analysis.filter(c => c.hasNif && c.hasRccm).length;
    const withNifOnly = analysis.filter(c => c.hasNif && !c.hasRccm).length;
    const withRccmOnly = analysis.filter(c => !c.hasNif && c.hasRccm).length;
    const withNone = totalCompanies - withBoth - withNifOnly - withRccmOnly;
    const withMissingInfo = withNone + withNifOnly + withRccmOnly;

    const top100Revenue = top100.reduce((s, c) => s + c.totalTTC, 0);
    const top100Percentage = totalRevenue > 0 ? (top100Revenue / totalRevenue) * 100 : 0;

    return {
      totalCompanies, withBoth, withNone, withNifOnly, withRccmOnly,
      withMissingInfo,
      completionRate: (withBoth / totalCompanies) * 100,
      missingRate: (withMissingInfo / totalCompanies) * 100,
      totalRevenue, totalTax: analysis.reduce((s, c) => s + c.taxAmount, 0),
      averageRevenue, maxRevenue, minRevenue,
      medianRevenue: median, stdDevRevenue: stdDev, cv, q1, q3,
      top100Count: top100.length, top100Revenue, top100Percentage,
      hhi,
    };
  }, [analysis, top100]);

  const donutData = useMemo(() => [
    { name: 'NIF & RCCM', value: stats.withBoth, color: '#34d399' },
    { name: 'Manque NIF', value: stats.withRccmOnly, color: '#fbbf24' },
    { name: 'Manque RCCM', value: stats.withNifOnly, color: '#f97316' },
    { name: 'Manque les deux', value: stats.withNone, color: '#ef4444' },
  ], [stats]);

  const handleExport = () => {
    if (!top100.length) {
      setError('Aucune donnée à exporter');
      setTimeout(() => setError(null), 3000);
      return;
    }
    const headers = ['Rang', 'Entreprise', 'Adresse', 'NIF', 'RCCM', 'Total HT', 'TVA', 'Total TTC'];
    const csvData = top100.map((company, index) => [
      index + 1,
      company.name || '',
      company.address || '',
      company.hasNif ? company.nif : 'Manquant',
      company.hasRccm ? company.rccm : 'Manquant',
      formatCurrency(company.totalHT),
      formatCurrency(company.taxAmount),
      formatCurrency(company.totalTTC),
    ]);
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `top100_entreprises_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setSuccessMessage('Export CSV effectué');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  if (loading && !data.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-400/20 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-slate-300">Chargement des entreprises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950/50 to-indigo-950 relative">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto p-3 md:p-6 pb-24 md:pb-6">
        {/* Cache info */}
        <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-3 md:p-4 mb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-xs md:text-sm text-slate-300">
          <div className="flex items-center gap-2 flex-wrap">
            <FiDatabase className="text-blue-400" />
            <span>{cacheDate ? `📦 Cache du ${formatDateCache(cacheDate)}` : '🔄 Données fraîches'}</span>
            <FiClock className="text-amber-400 ml-2" />
            <span>Cache 24h</span>
          </div>
          <button onClick={clearCacheAndReload} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-xs">Vider cache</button>
        </div>

        {/* Desktop header */}
        <div className="hidden md:block mb-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent flex items-center gap-2">
                  <FiHome /> Analyse des Entreprises <FiTrendingUp />
                </h1>
                <p className="text-slate-400 text-sm mt-1">Statistiques, conformité fiscale et Top 100</p>
              </div>
              <div className="flex gap-3">
                <button onClick={manualRefresh} disabled={isRefreshing} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-sm disabled:opacity-50">
                  <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} /> {isRefreshing ? 'Mise à jour...' : 'Actualiser'}
                </button>
                <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/10 border border-blue-400/20 text-blue-400 rounded-xl text-sm">
                  <FiDownload /> Exporter
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mobile header */}
        <div className="md:hidden mb-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Entreprises</h1>
            <p className="text-slate-400 text-xs">{stats.totalCompanies} entreprises</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
              <FiMenu size={18} />
            </button>
          </div>
        </div>

        {/* Stats cards - basic */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {[
            { label: 'Total', value: stats.totalCompanies, icon: FiHome, color: 'text-blue-400' },
            { label: 'Complètes', value: stats.withBoth, icon: FiCheckCircle, color: 'text-emerald-400' },
            { label: 'Incomplètes', value: stats.withMissingInfo, icon: FiAlertCircle, color: 'text-amber-400' },
            { label: 'CA Total', value: formatCurrency(stats.totalRevenue), icon: FiDollarSign, color: 'text-emerald-400' },
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Stats cards - dispersion/concentration */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-4 md:mb-8">
          {[
            { label: 'Médiane CA', value: formatCurrency(stats.medianRevenue), icon: FiPercent, color: 'text-cyan-400' },
            { label: 'Écart-type', value: formatCurrency(stats.stdDevRevenue), icon: FiActivity, color: 'text-amber-400' },
            { label: 'Q1 - Q3', value: `${formatCurrency(stats.q1)} - ${formatCurrency(stats.q3)}`, icon: FiTarget, color: 'text-indigo-400' },
            { label: 'Indice HHI', value: stats.hhi.toFixed(0), icon: FiLayers, color: 'text-orange-400' },
          ].map((s, i) => (
            <div key={i} className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-2xl p-4 md:p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-blue-400/50 to-purple-400/50" />
              <s.icon className={`text-2xl mb-2 ${s.color}`} />
              <div className="text-white font-bold text-lg md:text-xl">{s.value}</div>
              <div className="text-slate-400 text-xs">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiPieChart className="text-violet-400" /> Complétude fiscale</h3>
            <DonutChart data={donutData} total={stats.totalCompanies} />
          </div>
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><FiBarChart2 className="text-amber-400" /> Taux de complétion</h3>
            <div className="space-y-4">
              <ProgressBar label="NIF + RCCM" value={stats.withBoth} total={stats.totalCompanies} color="#34d399" />
              <ProgressBar label="Manque NIF" value={stats.withRccmOnly} total={stats.totalCompanies} color="#fbbf24" />
              <ProgressBar label="Manque RCCM" value={stats.withNifOnly} total={stats.totalCompanies} color="#f97316" />
              <ProgressBar label="Manque les deux" value={stats.withNone} total={stats.totalCompanies} color="#ef4444" />
            </div>
          </div>
        </div>

        {/* Top 100 Desktop */}
        <div className="hidden md:block">
          <div className="bg-gradient-to-br from-blue-950/40 via-indigo-950/30 to-slate-900/40 backdrop-blur-xl border border-blue-400/20 rounded-3xl p-5 mb-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2"><FiTrendingUp className="text-green-400" /> Top 100 entreprises</h3>
              <div className="relative w-full md:w-64">
                <FiSearch className="absolute left-3 top-3 text-slate-500" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-slate-400">
                    <th className="p-3">Rang</th>
                    <th className="p-3">Entreprise</th>
                    <th className="p-3">NIF</th>
                    <th className="p-3">RCCM</th>
                    <th className="p-3 text-right">CA TTC</th>
                    <th className="p-3 text-right">Part Top 100</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCompanies.map((company, idx) => {
                    const rank = indexOfFirstItem + idx + 1;
                    const part = stats.top100Revenue > 0 ? (company.totalTTC / stats.top100Revenue) * 100 : 0;
                    const rankStyle = rank === 1 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-black' :
                                      rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' :
                                      rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                                      'bg-blue-500/20 text-blue-400';
                    return (
                      <tr key={rank} className="border-b border-white/5 hover:bg-white/5 cursor-pointer" onClick={() => setSelectedCompany(company)}>
                        <td className="p-3">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${rankStyle}`}>
                            {rank <= 3 ? <FiAward size={14} /> : rank}
                          </span>
                        </td>
                        <td className="p-3 text-white font-medium">{company.name}</td>
                        <td className="p-3">
                          {company.hasNif ? (
                            <Badge color="green"><FiCheckCircle size={12} /> {company.nif}</Badge>
                          ) : (
                            <Badge color="red"><FiXCircle size={12} /> Manquant</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          {company.hasRccm ? (
                            <Badge color="green"><FiCheckCircle size={12} /> {company.rccm}</Badge>
                          ) : (
                            <Badge color="red"><FiXCircle size={12} /> Manquant</Badge>
                          )}
                        </td>
                        <td className="p-3 text-right text-emerald-400 font-mono">{formatCurrency(company.totalTTC)}</td>
                        <td className="p-3 text-right text-slate-400">{part.toFixed(2)}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-white/10 mt-4">
                <div className="text-sm text-slate-400">{indexOfFirstItem + 1}–{Math.min(indexOfFirstItem + itemsPerPage, filteredTop100.length)} sur {filteredTop100.length}</div>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsLeft size={16} /></button>
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => setCurrentPage(pageNum)} className={`w-8 h-8 rounded-lg text-sm font-medium ${currentPage === pageNum ? 'bg-blue-500/20 text-blue-400 border border-blue-400/30' : 'text-slate-400 hover:bg-white/10'}`}>{pageNum}</button>
                    );
                  })}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronsRight size={16} /></button>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <span>Afficher</span>
                  <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white">
                    <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top 100 Mobile */}
        <div className="md:hidden space-y-3">
          <div className="relative mb-4">
            <FiSearch className="absolute left-4 top-3 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-blue-400/20 rounded-2xl text-white placeholder-slate-500"
            />
          </div>
          {currentCompanies.map((company, idx) => (
            <CompanyCardMobile
              key={idx}
              company={company}
              rank={indexOfFirstItem + idx + 1}
              onClick={setSelectedCompany}
            />
          ))}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 py-4">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronLeft size={16} /></button>
              <span className="text-slate-400 text-sm">Page {currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30"><FiChevronRight size={16} /></button>
            </div>
          )}
        </div>
      </div>

      {/* Modal détails */}
      <AnimatePresence>
        {selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedCompany(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-b from-blue-950/95 to-slate-900/95 backdrop-blur-xl border border-blue-400/20 rounded-3xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2"><FiHome className="text-blue-400" /> {selectedCompany.name}</h2>
                <button onClick={() => setSelectedCompany(null)} className="p-2 rounded-xl bg-white/5 text-slate-400 hover:text-white"><FiX size={20} /></button>
              </div>
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2"><FiMapPin /> Adresse</h3>
                  <p className="text-slate-300">{selectedCompany.address || 'Non renseignée'}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2"><FiCreditCard /> NIF</h3>
                    {selectedCompany.hasNif ? (
                      <Badge color="green"><FiCheckCircle size={12} /> {selectedCompany.nif}</Badge>
                    ) : (
                      <Badge color="red"><FiXCircle size={12} /> Manquant</Badge>
                    )}
                  </div>
                  <div>
                    <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2"><FiCreditCard /> RCCM</h3>
                    {selectedCompany.hasRccm ? (
                      <Badge color="green"><FiCheckCircle size={12} /> {selectedCompany.rccm}</Badge>
                    ) : (
                      <Badge color="red"><FiXCircle size={12} /> Manquant</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-blue-400 font-semibold mb-2 flex items-center gap-2"><FiDollarSign /> Performances</h3>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-slate-400">HT</div>
                      <div className="text-white font-bold">{formatCurrency(selectedCompany.totalHT)}</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-slate-400">TVA</div>
                      <div className="text-amber-400 font-bold">{formatCurrency(selectedCompany.taxAmount)}</div>
                    </div>
                    <div className="bg-white/5 rounded-xl p-3 text-center">
                      <div className="text-xs text-slate-400">TTC</div>
                      <div className="text-emerald-400 font-bold">{formatCurrency(selectedCompany.totalTTC)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        onRefresh={manualRefresh}
        onClearCache={clearCacheAndReload}
        onExport={handleExport}
        isRefreshing={isRefreshing}
      />

      {/* Notifications */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed top-4 right-4 z-50 px-5 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-2xl backdrop-blur-xl text-sm"
          >
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="fixed top-4 right-4 z-50 px-5 py-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-2xl backdrop-blur-xl text-sm"
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Indicateur de connexion */}
      <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 backdrop-blur-xl border ${
        isOnline ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
      }`}>
        <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
        <span>{isOnline ? 'En ligne' : 'Hors ligne'}</span>
      </div>
    </div>
  );
};

export default CompanyAnalytics;